import { CommonModule, TitleCasePipe } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewChildren, QueryList, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import _ from "lodash";
import { MarkdownModule } from "ngx-markdown";
import { ConfirmationService, MenuItem } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatepickerComponent } from '../../../@core/components/ui/datepicker/datepicker.component';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { DrawerModule } from "primeng/drawer";
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { Menu, MenuModule } from 'primeng/menu';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { PopoverModule } from "primeng/popover";
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { DeleteConfirmationComponent } from '../../../@core/components/ui/delete-confirmation/delete-confirmation.component';
import { PopoverComponent } from '../../../@core/components/ui/popover/popover.component';
import { SortPopoverComponent, SortFieldOption } from '../../../@shared/components/sort-popover/sort-popover.component';
import { SelectComponent, SelectOption, SelectConfig, SelectSearchEvent } from '../../../@core/components/ui/select';
import { SelectButtonComponent } from '../../../@core/components/ui/select-button/select-button.component';
import { USER_ROLE } from '../../../@core/models/enums';
import { IProject, IStatus, IUser, IWorkspace, SHARE_ACCESS } from '../../../@core/models/model';
import { AuthenticationService } from '../../../@core/services/authentication.service';
import { SubscriptionStateService } from '../../../@core/services/subscription-state.service';
import { FeatureFlagService } from '../../../@core/services/feature-flag.service';
import { SOCKET_EVENTS, SocketService } from '../../../@core/services/socket.service';
import { ToastService } from '../../../@core/services/toast.service';
import { AiAssistantComponent } from "../../../@shared/components/ai-assistant/ai-assistant.component";
import { DocumentCardComponent } from '../../../@shared/components/document-card/document-card.component';
import { GridListViewComponent } from '../../../@shared/components/grid-list-view/grid-list-view.component';
import { SearchBarComponent } from '../../../@shared/components/search-bar/search-bar.component';
import { ThrottleClickDirective } from '../../../@shared/directives/throttle-click.directive';
import { TableColumn, TableConfig } from '../../../@shared/models/table.models';
import { DateFormatterPipe } from '../../../@shared/pipes/dateFormater.pipe';
import { escapeRegExp } from '../../../common/utils';
import { CreateFormComponent } from '../../../form-management/create-form/create-form.component';
import { PENDING_STATUS, CONTRACT_DOCUMENTS, CONTRACT_TYPE, IContract } from '../../models/contract-management.model';
import { IDocumentType } from '../../models/settings.model';
import { ContractService, IContractResponse } from '../../services/contracts.service';
import { DocumentTypeFilter, DocumentTypeService } from '../../services/document-type.service';
import { INotification, NOTIFICATION_EVENTS } from '../../services/notification.service';
import { OrganizationSetupFormService } from '../../services/organization-setup-from.service';
import { ProjectService } from '../../services/project.service';
import { UserService } from '../../services/user.service';
import { STANDARD_DOCUMENT_STATUS } from '../settings/documents/document-status/document-status.enum';
import { DOCUMENTS_TABLE_COLUMNS, DOCUMENTS_TABLE_CONFIG } from './documents-table.config';
import { SegmentedOption } from 'src/app/@core/components/ui/segmented/segmented.component';
import { UI_FEATURE } from 'src/app/@core/models/feature-mapping';
import { CustomMenuItem, MenuComponent } from 'src/app/@core/components/ui/menu/menu.component';
import { IconSourceEnum } from 'src/app/@core/components/ui/icon/icon.component';
import { TabConfig } from 'src/app/@shared/components/file-selection';
import { FileUploadWithActionsComponent } from 'src/app/@shared/components/file-upload-with-actions/file-upload-with-actions.component';
import { FileDropTypes } from 'src/app/@shared/components/file-drop/file-drop.types';
import { AssetUrlPipe } from 'src/app/@shared/pipes/asset-url.pipe';
import { RepositoryService } from '../../services/repository.service';
import { KnowledgeBaseDialogComponent } from '../../../@shared/components/knowledge-base-dialog/knowledge-base-dialog.component';
import { KNOWLEDGE_BASE_SVG } from '../settings/knowledgebase/knowledge-base-svg';
import { UserNameDisplayComponent } from 'src/app/@shared/components/user-name-display/user-name-display.component';
import { AvatarStackComponent } from 'src/app/@shared/components/avatar-stack/avatar-stack.component';

@Component({
    selector: 'app-documents',
    standalone: true,
    imports: [ButtonComponent, RouterModule, CommonModule, MenuModule, FormsModule, SkeletonModule, SelectModule, ConfirmDialogModule, DialogComponent,
        InputGroupAddonModule, InputGroupModule, DateFormatterPipe, PaginatorModule, MarkdownModule, TooltipModule, ProgressBarModule, AiAssistantComponent, ThrottleClickDirective, ChipModule, CreateFormComponent,
        TabsModule, DatepickerComponent, CheckboxModule, SearchBarComponent, PopoverModule, DrawerModule, TitleCasePipe, AvatarModule, AvatarGroupModule, DocumentCardComponent, SelectComponent, DeleteConfirmationComponent,
        SelectButtonComponent, GridListViewComponent, PopoverComponent, MenuComponent, FileUploadWithActionsComponent, AssetUrlPipe, KnowledgeBaseDialogComponent, SortPopoverComponent, AvatarStackComponent
    ],
    templateUrl: './documents.component.html',
    providers: [ConfirmationService],
    styleUrl: './documents.component.scss'
})

export class DocumentsComponent implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild('menu') menu!: Menu;
    @ViewChildren(DocumentCardComponent) documentCards!: QueryList<DocumentCardComponent>;

    // Template References for Table View
    @ViewChild('documentNameTemplateRef') documentNameTemplateRef!: TemplateRef<any>;
    @ViewChild('projectTemplateRef') projectTemplateRef!: TemplateRef<any>;
    @ViewChild('documentTypeTemplateRef') documentTypeTemplateRef!: TemplateRef<any>;
    @ViewChild('assigneesTemplateRef') assigneesTemplateRef!: TemplateRef<any>;
    @ViewChild('statusTemplateRef') statusTemplateRef!: TemplateRef<any>;
    @ViewChild('dueDateTemplateRef') dueDateTemplateRef!: TemplateRef<any>;
    @ViewChild('upcomingMilestoneTemplateRef') upcomingMilestoneTemplateRef!: TemplateRef<any>;
    @ViewChild('dateUpdatedTemplateRef') dateUpdatedTemplateRef!: TemplateRef<any>;
    @ViewChild('actionsTemplateRef') actionsTemplateRef!: TemplateRef<any>;
    @ViewChild('optionsMenu') optionsMenu!: MenuComponent;

    currentUser !: IUser;
    currentWorkspace!: IWorkspace | null | undefined
    contractMenuItems: MenuItem[] | undefined;
    isLoading: boolean = false
    searchValue: string = ''
    contracts: IContractResponse[] = [];
    selectedContracts: IContractResponse[] = [];

    // View Mode
    viewMode: 'grid' | 'list' = 'grid';
    viewModeOptions: SegmentedOption[] = [
        {
            label: 'List',
            value: 'list',
            icon: 'pi pi-bars'
        },
        {
            label: 'Grid',
            value: 'grid',
            icon: 'pi pi-th-large'
        }
    ];

    // Table Configuration
    tableColumns: TableColumn[] = [...DOCUMENTS_TABLE_COLUMNS];
    tableConfig: TableConfig = { ...DOCUMENTS_TABLE_CONFIG };
    contractOptions: SelectOption[] = [];
    statusOptions: SelectOption[] = [];
    peopleOptions: SelectOption[] = [];
    headerText: string = CONTRACT_DOCUMENTS.ALL
    selectedCode: CONTRACT_TYPE = CONTRACT_TYPE.ALL
    docCreationByDocumentType: { doc: IDocumentType | null, visible: boolean } = { doc: null, visible: false }
    aiAssistant: {
        open: boolean,
        content: string,
        response: string,
        stage: string,
        loading: boolean
        loadingPercentage: number
    } = {
            open: false, content: '', response: '', stage: 'Igniting creativity – Starting with AI', loading: false, loadingPercentage: 0
        };
    rename: {
        open: boolean,
        newName: IContract['name'],
        contractId: IContract['_id']
    } = { open: false, newName: '', contractId: '' };
    dueDate: {
        open: boolean,
        selectedDate: Date | null,
        contractId: IContract['_id']
    } = { open: false, selectedDate: null, contractId: '' };

    deleteDialogVisible = false;
    contractToDelete: { id: string, name: string } | null = null;
    createVisible: boolean = false
    docTypeSearch: string = ''
    docTypeLoading: boolean = false
    docTypes: IDocumentType[] = []
    filteredDocTypes: IDocumentType[] = []
    currentDate: Date = new Date();
    isLoadingCounts: boolean = true;

    //pagination
    defaultPaginatorValues = {
        first: 0,
        rows: 20
    };
    first: number = this.defaultPaginatorValues.first;
    rows: number = this.defaultPaginatorValues.rows;
    totalRecords: number = 0;

    contractsLength: number = 0
    openMenus: PopoverComponent[] = [];
    notificationSubscription: Subscription;
    USER_ROLE = USER_ROLE

    subscriptionDetails: any
    currentMenuDocument: { id: string, name: string } | null = null;
    currentlyOpenPopoverId: string | null = null;

    hasInnDocFeature(): boolean {
        return this.features.checkFeature(UI_FEATURE.DOCUMENTS).allowed;
    }

    openProjectLinkDialog: boolean = false

    projects: IProject[] = []
    selectedProject!: IProject | null
    selectedProjectId: string = ''
    selectedContract !: IContract | null

    isSharedWithWorkspace: boolean = false
    isSharedWithProject: boolean = false

    users: IUser[] = [];
    selectedPeople: string | null = null;
    sortField: 'name' | 'createdAt' | 'dueDate' | 'lastEdited' | 'updatedAt' = 'updatedAt';
    sortOrder: 'asc' | 'desc' = 'desc';
    readonly sortFields: SortFieldOption<'name' | 'createdAt' | 'dueDate' | 'updatedAt'>[] = [
        { label: 'Document Name', value: 'name' },
        { label: 'Date Created', value: 'createdAt' },
        { label: 'Due Date', value: 'dueDate' },
        { label: 'Updated At', value: 'updatedAt' },
    ];
    filterDueDateRange: Date[] | null = null;
    filterCreatedDateRange: Date[] | null = null;
    filterPanelVisible = false;
    selectedStatus: STANDARD_DOCUMENT_STATUS | null = null;
    selectedActionFilter: PENDING_STATUS | null = null;
    PENDING_METRIC = PENDING_STATUS;

    actionFilterOptions: SelectOption[] = [
        { label: 'Overdue', value: PENDING_STATUS.OVERDUE },
        { label: 'Pending Approvals', value: PENDING_STATUS.PENDING_APPROVALS },
        { label: 'Pending Signatures', value: PENDING_STATUS.PENDING_SIGNATURES },
    ];

    actionFilterSelectConfig: SelectConfig = {
        placeholder: 'Select status type',
        searchable: false,
        clearable: true,
        style: { 'width': '100%' }
    };

    docTypeOptions: SelectOption[] = [];
    selectedDocTypeId: string | null = null;

    projectOptions: SelectOption[] = [];

    contractSelectConfig: SelectConfig = {
        placeholder: 'Select option',
        searchable: false,
        clearable: false,
        style: { 'width': '100%' }
    };

    peopleSelectConfig: SelectConfig = {
        placeholder: 'Select person',
        searchable: true,
        clearable: false,
        style: { 'width': '100%' }
    };

    docTypeSelectConfig: SelectConfig = {
        placeholder: 'Select document type',
        searchable: true,
        clearable: false,
        style: { 'width': '100%' }
    };

    statusSelectConfig: SelectConfig = {
        placeholder: 'Select status',
        searchable: true,
        clearable: false,
        style: { 'width': '100%' }
    };

    projectSelectConfig: SelectConfig = {
        placeholder: 'Select a Project',
        searchable: true,
        clearable: false,
        styleClass: 'dropdown border',
        appendTo: 'body'
    };

    isOptionsMenuOpen: boolean = false;
    optionsMenuItems: CustomMenuItem[] = [
        {
            label: 'Create document',
            value: 0,
            icon: 'pi pi-plus',
            iconSource: IconSourceEnum.PRIMENG,
        },
        {
            label: 'Upload multiple documents',
            value: 1,
            icon: 'pi pi-upload',
            iconSource: IconSourceEnum.PRIMENG,
        },
    ]

    selectedTabIndex = signal<number>(0);
    uploadedFiles: File[] = [];
    loadingState = signal<boolean>(false);
    allowedFileTypes: string[] = [FileDropTypes.PDF, FileDropTypes.DOCX, FileDropTypes.DOC, FileDropTypes.XLSX, FileDropTypes.XLS];

    createDocumentDialogTabs = signal<TabConfig[]>([
        {
            id: '0',
            label: 'Create document',
            tooltip: 'Create a new document'
        },
        {
            id: '1',
            label: 'Upload multiple documents',
            tooltip: 'Upload multiple documents from your local machine'
        }

    ])

    //#region LOADING STATES
    isCreateDocumentLoading = signal<boolean>(false);
    //#endregion

    constructor(
        private socketService: SocketService,
        private contractService: ContractService,
        private authService: AuthenticationService,
        private subscriptionState: SubscriptionStateService,
        private toastService: ToastService,
        private router: Router,
        private route: ActivatedRoute,
        private confirmationService: ConfirmationService,
        private userService: UserService,
        private documentTypeService: DocumentTypeService,
        private organizationService: OrganizationSetupFormService,
        private projectService: ProjectService,
        private features: FeatureFlagService,
        private repositoryService: RepositoryService
    ) {
        this.currentUser = authService.getUser() as IUser;
        this.organizationService.currentWorkspace.subscribe(res => {
            this.currentWorkspace = res
        });


        this.contractOptions = [
            { label: 'All', value: CONTRACT_TYPE.ALL },
            { label: 'Created by me', value: CONTRACT_TYPE.DRAFTED },
            { label: 'Shared with me', value: CONTRACT_TYPE.SHARED },
        ]
        this.notificationSubscription = this.socketService.on<{ notification: INotification }>(SOCKET_EVENTS.NOTIFICATION).subscribe(res => {
            const notification = res[0].notification
            if (notification.subModule == NOTIFICATION_EVENTS.DOCUMENT.GENERATED) {
                const contract = (notification.event.data as { contractData: IContract }).contractData
                if ((this.defaultPaginatorValues.first == 0 &&
                    this.searchValue == '' ||
                    escapeRegExp(contract.name).toLowerCase().includes(this.searchValue.trim())) &&
                    (this.currentWorkspace?._id == contract.workspace)
                ) {
                    this.contracts.unshift(contract as IContractResponse);
                    if (this.contractsLength < this.defaultPaginatorValues.rows)
                        this.contractsLength++;
                    else
                        this.contracts.pop();
                }
            }
        })
    }

    ngOnInit() {
        this.checkSubscriptionStatus();
        this.getAllStatuses();
        if (this.canFilterByAllUsers) {
            this.getAllUsersOfOrganization();
        }
        this.getAllDocumentTypes();
        this.getProjects();

        const initialParams = this.route.snapshot.queryParams;
        if (initialParams['actionFilter']) {
            this.selectedActionFilter = initialParams['actionFilter'] as PENDING_STATUS;
        }

        if (initialParams['scopeToMe'] === 'true' && this.currentUser?._id) {
            this.selectedPeople = this.currentUser._id;
        }
        this.fetchContracts(this.defaultPaginatorValues.first, this.defaultPaginatorValues.rows);
        this.isLoadingCounts = false;

        // Check if we should automatically open the create document dialog
        this.route.queryParams.subscribe(params => {
            if (params['create'] === 'true') {
                // Open the dialog first
                setTimeout(() => {
                    this.createVisible = true;
                    if (this.docTypes.length === 0) {
                        this.docTypeLoading = true;
                        this.documentTypeService.getDocTypes([DocumentTypeFilter.GENERAL, DocumentTypeFilter.FORM_RELATED]).subscribe(res => {
                            if (res && res.success) {
                                this.docTypeLoading = false;
                                this.docTypes = res.data || [];
                                this.filteredDocTypes = this.docTypes;
                            } else {
                                this.docTypeLoading = false;
                            }
                        });
                    }
                    // Remove the query param from URL after dialog is opened
                    this.router.navigate([], {
                        relativeTo: this.route,
                        queryParams: {},
                        replaceUrl: true
                    });
                }, 100);
            }

            if (params['openAction'] === 'questionnaire') {
                this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
                this.documentTypeService.getDocTypes([DocumentTypeFilter.FORM_RELATED]).subscribe(res => {
                    if (res?.success) {
                        const formDocType = (res.data || []).find((dt: IDocumentType) => dt.containsForm);
                        if (formDocType) {
                            this.selectDocType(formDocType);
                        }
                    }
                });
            }

            if (params['actionFilter']) {
                this.selectedActionFilter = params['actionFilter'] as PENDING_STATUS;
                if (params['scopeToMe'] === 'true' && this.currentUser?._id) {
                    this.selectedPeople = this.currentUser._id;
                }
                this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
            }
        });
    }

    ngAfterViewInit() {
        // Build templates map for data table
        this.buildTemplatesMap();
    }

    get uploadDocumentTypeOptions(): SelectOption[] {
        return this.docTypeOptions.filter(docType => docType.data.containsForm === false);
    }

    private buildTemplatesMap() {
        // This method can be used for any additional template setup if needed
    }

    get templatesMap(): { [key: string]: TemplateRef<any> } {
        return {
            'documentNameTemplate': this.documentNameTemplateRef,
            'projectTemplate': this.projectTemplateRef,
            'documentTypeTemplate': this.documentTypeTemplateRef,
            'assigneesTemplate': this.assigneesTemplateRef,
            'statusTemplate': this.statusTemplateRef,
            'dueDateTemplate': this.dueDateTemplateRef,
            'upcomingMilestoneTemplate': this.upcomingMilestoneTemplateRef,
            'dateUpdatedTemplate': this.dateUpdatedTemplateRef,
            'actionsTemplate': this.actionsTemplateRef
        };
    }

    onViewModeChange(mode: 'grid' | 'list') {
        this.viewMode = mode;
    }

    getMenuItems(document: IContractResponse) {

        this.contractMenuItems = [
            {
                label: 'Options',
                items: []
            }
        ];

        // Add knowledge base options for all users
        this.contractMenuItems[0].items?.push({
            label: 'Add to workspace knowledge',
            icon: 'pi pi-book',
        }, {
            label: 'Add to project knowledge',
            icon: 'pi pi-bookmark',
        });

        const currentUserRoles = Array.isArray((document)?.currentUserRole) ? (document).currentUserRole : ((document)?.currentUserRole ? [(document).currentUserRole] : []);
        const canManage = currentUserRoles.includes(SHARE_ACCESS.ADMIN) || currentUserRoles.includes(SHARE_ACCESS.OWNER) || currentUserRoles.includes(SHARE_ACCESS.EDIT) || currentUserRoles.includes(SHARE_ACCESS.ORG_ADMIN);
        const canDelete = currentUserRoles.includes(SHARE_ACCESS.ADMIN) || currentUserRoles.includes(SHARE_ACCESS.ORG_ADMIN) || currentUserRoles.includes(SHARE_ACCESS.OWNER);

        if (canManage) {
            if (!document.project) {
                this.contractMenuItems[0].items?.push({
                    label: 'Link to Project',
                    icon: 'pi pi-link',
                })
            }

            this.contractMenuItems[0].items?.push(
                {
                    label: 'Rename',
                    icon: 'pi pi-pencil'
                },
                {
                    label: 'Due Date',
                    icon: 'pi pi-calendar'
                },
                // Only allow 'Delete' for ADMIN, ORG_ADMIN and OWNER
                ...(canDelete ? [{
                    label: 'Delete',
                    icon: 'pi pi-trash',
                    class: 'text-red-500'
                }] : [])
            )


        }


        return this.contractMenuItems
    }

    getAllUsersOfOrganization() {
        this.userService.getUsers().subscribe((res) => {
            this.users = res.data.users || [];
            this.peopleOptions = this.users.map(user => ({
                label: user.firstName ? (user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName) : (user.lastName ? user.lastName : user.email),
                value: user._id,
                data: user
            }));
        })
    }

    handlePeopleChange(event: any) {
        this.selectedPeople = event.value;
    }


    /**
     * @deprecated Use loadSubscriptionDetails() instead - no longer makes API calls
     */
    checkSubscriptionStatus() {
        // Now uses cached data
        this.loadSubscriptionDetails();
    }

    /**
     * Load subscription details from cached state
     * No API call - uses data already loaded in AppLayoutComponent
     */
    loadSubscriptionDetails() {
        const details = this.subscriptionState.subscriptionDetails();
        if (details) {
            this.subscriptionDetails = details;
        }
    }

    onPageChange(event: PaginatorState) {
        this.first = event.first ?? this.defaultPaginatorValues.first;
        this.rows = event.rows ?? this.defaultPaginatorValues.rows;
        this.fetchContracts(this.first, this.rows)
    }

    fetchContracts(skip = this.defaultPaginatorValues.first, limit = this.defaultPaginatorValues.rows) {
        this.isLoading = true
        this.tableConfig = { ...this.tableConfig, loading: true };

        const filters: any = {
            type: this.selectedCode,
            skip,
            limit,
            name: this.searchValue.trim(),
            sortField: this.sortField,
            sortOrder: this.sortOrder
        };

        if (this.selectedPeople) {
            filters.createdBy = this.selectedPeople;
        }

        if (this.selectedDocTypeId) {
            filters.linkedDocType = this.selectedDocTypeId;
        }

        if (this.selectedStatus) {
            filters.documentStatus = this.selectedStatus;
        }

        if (this.filterDueDateRange && this.filterDueDateRange.length === 2) {
            filters.dueDateStart = this.filterDueDateRange[0].toISOString();
            filters.dueDateEnd = this.filterDueDateRange[1].toISOString();
        }

        if (this.filterCreatedDateRange && this.filterCreatedDateRange.length === 2) {
            filters.createdDateStart = this.filterCreatedDateRange[0].toISOString();
            filters.createdDateEnd = this.filterCreatedDateRange[1].toISOString();
        }

        if (this.selectedActionFilter) {
            filters.actionFilter = this.selectedActionFilter;
        }

        this.contractService.getContracts(filters).subscribe((res) => {
            if (res && res.success) {
                this.isLoading = false
                this.tableConfig = { ...this.tableConfig, loading: false };
                const documents = res.data?.contracts.map(p => p.dueDate ? { ...p, dueDateExceeding: this.contractService.checkIfDueDateExceeding(p.dueDate) } : p)
                this.contracts = documents as IContractResponse[];
                this.totalRecords = res.data?.total || 0;
                this.contractsLength = this.totalRecords > this.defaultPaginatorValues.rows ? this.totalRecords : this.contracts.length;
            } else {
                this.isLoading = false;
                this.tableConfig = { ...this.tableConfig, loading: false };
                this.totalRecords = 0;
                this.toastService.error("Error fetching all contracts")
            }
        })
    }

    searchContract = _.debounce(() => {
        this.first = this.defaultPaginatorValues.first;
        this.rows = this.defaultPaginatorValues.rows;
        this.fetchContracts(this.first, this.rows)
    }, 500)

    onSearch() {
        this.isLoading = true;
        this.searchContract();
    }


    get canFilterByAllUsers(): boolean {
        return this.currentUser?.role === USER_ROLE.ADMIN
            || this.currentUser?.role === USER_ROLE.SUPER_ADMIN
            || this.currentUser?.workspaceRole === SHARE_ACCESS.ADMIN
            || this.currentUser?.workspaceRole === SHARE_ACCESS.OWNER;
    }

    get canManageDocTypes(): boolean {
        return this.currentUser?.role === USER_ROLE.ADMIN
            || this.currentUser?.role === USER_ROLE.SUPER_ADMIN
            || this.currentUser?.workspaceRole === SHARE_ACCESS.ADMIN;
    }

    navigateToAddDocType() {
        this.router.navigate(['/settings/documents'], { queryParams: { action: 'add-doc-type' } });
    }


    onSortChange() {
        this.first = 0;
        this.fetchContracts(this.first, this.rows);
    }

    setSortField(field: 'name' | 'createdAt' | 'dueDate' | 'lastEdited') {
        this.sortField = field;
        this.onSortChange();
    }

    setSortOrder(order: 'asc' | 'desc') {
        this.sortOrder = order;
        this.onSortChange();
    }

    clearAllFilters() {
        this.filterDueDateRange = null;
        this.filterCreatedDateRange = null;
        this.selectedPeople = null;
        this.selectedDocTypeId = null;
        this.selectedStatus = null;
        this.selectedActionFilter = null;
        this.selectedCode = CONTRACT_TYPE.ALL;
        this.headerText = CONTRACT_DOCUMENTS.ALL;
        this.first = 0;
        this.fetchContracts(this.first, this.rows);
        this.filterPanelVisible = false;
    }

    handleActionFilterChange(event: any): void {
        this.selectedActionFilter = event.value ?? null;
    }

    applyFilters() {
        // Update header text based on selected document type
        if (this.selectedCode === CONTRACT_TYPE.DRAFTED) this.headerText = CONTRACT_DOCUMENTS.DRAFTED;
        else if (this.selectedCode === CONTRACT_TYPE.SHARED) this.headerText = CONTRACT_DOCUMENTS.SHARED;
        else if (this.selectedCode === CONTRACT_TYPE.ALL) this.headerText = CONTRACT_DOCUMENTS.ALL;

        this.first = 0;
        this.fetchContracts(this.first, this.rows);
        this.filterPanelVisible = false;
    }

    toggleFilterPanel() {
        this.filterPanelVisible = !this.filterPanelVisible;
    }

    getAllStatuses() {
        this.statusOptions = Object.values(STANDARD_DOCUMENT_STATUS).map((status) => ({
            label: status,
            value: status
        }))
    }

    handleStatusChange(event: any) {
        this.selectedStatus = event.value;
        // Filtering will be applied only when Apply Filter is clicked
    }

    getAllDocumentTypes() {
        this.documentTypeService.getDocTypes([DocumentTypeFilter.GENERAL, DocumentTypeFilter.FORM_RELATED]).subscribe((res) => {
            this.docTypes = res.data || [];
            this.filteredDocTypes = this.docTypes;
            this.docTypeOptions = this.docTypes.map(docType => ({
                label: docType.name,
                value: docType._id,
                data: docType
            }));
        })
    }

    handleDocTypeChange(event: any) {
        this.selectedDocTypeId = event.value;
    }

    newDocument() {
        // TODO: Uncomment this when we have a subscription model
        // if (this.subscriptionDetails.plan.maxDocuments <= this.contractsLength) {
        //   this.toastService.warn("You have reached the maximum number of documents allowed in your plan. Please delete an existing document to create a new one.");
        //   return
        // }
        this.createVisible = !this.createVisible
        if (this.docTypes.length != 0)
            return
        this.docTypeLoading = true
        this.documentTypeService.getDocTypes([DocumentTypeFilter.GENERAL, DocumentTypeFilter.FORM_RELATED]).subscribe(res => {
            if (res && res.success) {
                this.docTypeLoading = false
                this.docTypes = res.data || []
                this.filteredDocTypes = this.docTypes
            }
            else
                this.docTypeLoading = false
        })
    }

    onDocTypeFilter() {
        this.filteredDocTypes = this.docTypes.filter((docType: IDocumentType) => docType.name.toLowerCase().includes(this.docTypeSearch.toLowerCase()));
    }

    onCreateDocumentHide() {
        this.docTypeSearch = '';
        this.filteredDocTypes = this.docTypes;
    }

    onDocCreationVisibleChange(isVisible: boolean) {
        if (!isVisible) {
            this.isCreateDocumentLoading.set(false);
            this.docCreationByDocumentType = { doc: null, visible: false };
        }
    }

    selectDocType(docType: IDocumentType) {
        if (this.isCreateDocumentLoading()) {
            return;
        }
        this.isCreateDocumentLoading.set(true);
        if (docType.containsForm) {
            this.docCreationByDocumentType = { doc: docType, visible: true }
        } else {
            let form: FormData = new FormData();
            form.set('name', 'Untitled')
            form.set('linkedDocType', docType._id)
            if (this.isSharedWithWorkspace) {
                form.set('sharedWithWorkspace', 'true')
            }
            if (this.isSharedWithProject) {
                form.set('sharedWithProject', 'true')
            }

            this.contractService.createContract(form).subscribe((res) => {
                if (res && res.success) {
                    this.toastService.success("Document created successfully");
                    this.openContract(res.data._id)
                }
                this.isCreateDocumentLoading.set(false);
            })
        }
    }

    deleteContract(event: any, contractId: string, contractName: string) {
        this.contractToDelete = { id: contractId, name: contractName };
        this.deleteDialogVisible = true;
    }

    confirmDeleteContract() {
        if (this.contractToDelete) {
            this.contractService.deleteContract(this.contractToDelete.id).subscribe((res) => {
                if (res.success) {
                    this.toastService.success("Document deleted successfully");
                    this.fetchContracts(this.first, this.rows);
                    this.contractService.contractsUpdated.next();
                    this.deleteDialogVisible = false;
                    this.contractToDelete = null;
                } else {
                    this.toastService.error("Error deleting contract");
                    this.deleteDialogVisible = false;
                    this.contractToDelete = null;
                }
            });
        }
    }

    renameContract(event: Event) {
        if (!this.rename.newName.trim()) {
            this.rename.newName = 'Untitled';
        }
        this.contractService.updateContract(this.rename.contractId, { name: this.rename.newName }).subscribe(res => {
            if (res.success) {
                this.toastService.success("Document renamed successfully");
                this.fetchContracts(this.first, this.rows);
                this.contractService.contractsUpdated.next();
            } else {
                this.toastService.error("Error renaming contract");
            }
            this.resetRename();
        })
    }

    resetRename() {
        this.rename = {
            open: false,
            newName: '',
            contractId: ''
        }
    }

    saveDueDate(event: Event) {
        event.stopPropagation();
        const dueDateValue = this.dueDate.selectedDate || undefined;
        this.contractService.updateContract(this.dueDate.contractId, { dueDate: dueDateValue }).subscribe(res => {
            if (res.success) {
                this.toastService.success("Due Date updated successfully");
                const contractIndex = this.contracts.findIndex(contract => contract._id === this.dueDate.contractId);
                if (contractIndex !== -1) {
                    this.contracts[contractIndex] = {
                        ...this.contracts[contractIndex],
                        dueDate: dueDateValue
                    };
                }
                this.contractService.contractsUpdated.next();
            } else {
                this.toastService.error("Error updating Due Date");
            }
            this.resetDueDate();
            this.menu.hide();
        })
    }

    resetDueDate() {
        this.dueDate = {
            open: false,
            selectedDate: null,
            contractId: ''
        }
    }

    openContract(contractId: string) {
        this.router.navigate(['edit-document'], {
            queryParams: { 'documentId': contractId }
        });
    }

    ngOnDestroy(): void {
        if (this.notificationSubscription)
            this.notificationSubscription.unsubscribe()
    }

    handleChange(event: any): void {
        this.selectedCode = event.value;
        // Header text and filtering will be updated only when Apply Filter is clicked
    }

    handleMenuClick(event: Event, contractId: string, contractName: string, item: MenuItem) {
        event.stopPropagation();
        if (item.label === 'Delete') {
            this.deleteContract(event, contractId, contractName);
        } else if (item.label === 'Rename') {
            this.rename = {
                open: true,
                newName: '',
                contractId
            }
        } else if (item.label === 'Due Date') {
            const contract = this.contracts.find(contract => contract._id === contractId);
            this.dueDate = {
                open: true,
                selectedDate: contract?.dueDate ? new Date(contract.dueDate) : null,
                contractId
            }
        }
        else if (item.label === 'Link to Project') {
            this.selectedContract = this.contracts.find(contract => contract._id === contractId) ?? null
            this.openProjectLinkDialog = true
        } else if (item.label === 'Add to workspace knowledge') {
            this.currentMenuDocument = { id: contractId, name: contractName };
            this.openAddToWorkspaceKnowledgeDialog();
        } else if (item.label === 'Add to project knowledge') {
            this.currentMenuDocument = { id: contractId, name: contractName };
            this.openAddToProjectKnowledgeDialog();
        }
    }

    handleClick(event: MouseEvent, menu: PopoverComponent) {
        // Close all previously open menus
        this.openMenus.forEach((openMenu) => openMenu.hide());
        this.openMenus = [];

        // Open the new menu
        menu.toggle(event);
        this.openMenus.push(menu);
    }

    handlePopoverToggle(event: { documentId: string, isOpen: boolean }) {
        if (event.isOpen) {
            // Close any previously open popover before opening a new one
            if (this.currentlyOpenPopoverId && this.currentlyOpenPopoverId !== event.documentId) {
                const previousCard = this.documentCards?.find(card => card.document._id === this.currentlyOpenPopoverId);
                previousCard?.closePopover();
            }
            this.currentlyOpenPopoverId = event.documentId;
        } else if (this.currentlyOpenPopoverId === event.documentId) {
            // Only clear if the currently tracked popover was closed
            this.currentlyOpenPopoverId = null;
        }
    }

    openDocumentMenu(documentId: string, documentName: string) {
        this.currentMenuDocument = { id: documentId, name: documentName };
    }

    handleMenuAction(action: string, menu: PopoverComponent) {
        this.handleMenuItemClick(action);
        menu.hide();
    }

    handleMenuItemClick(action: string) {
        if (!this.currentMenuDocument) return;

        const contractId = this.currentMenuDocument.id;
        const contractName = this.currentMenuDocument.name;

        switch (action) {
            case 'Delete':
                this.deleteContract(new Event('click'), contractId, contractName);
                this.currentMenuDocument = null;
                break;
            case 'Rename':
                this.rename = {
                    open: true,
                    newName: '',
                    contractId
                };
                this.currentMenuDocument = null;
                break;
            case 'Due Date':
                const contract = this.contracts.find(c => c._id === contractId);
                this.dueDate = {
                    open: true,
                    selectedDate: contract?.dueDate ? new Date(contract.dueDate) : null,
                    contractId
                };
                this.currentMenuDocument = null;
                break;
            case 'Link to Project':
                this.selectedContract = this.contracts.find(c => c._id === contractId) ?? null;
                this.openProjectLinkDialog = true;
                this.currentMenuDocument = null;
                break;
            case 'Add to workspace knowledge':
                this.openAddToWorkspaceKnowledgeDialog();
                break;
            case 'Add to project knowledge':
                this.openAddToProjectKnowledgeDialog();
                break;
        }
    }


    getProjects(searchValue?: string) {
        this.projectService.getProjects(0, 1000, {
            active: true,
            ...(searchValue && { searchValue })
        }).subscribe((res) => {
            if (res) {
                this.projects = res.data.projects;
                this.projectOptions = this.projects.map(project => ({
                    label: project.name,
                    value: project._id,
                    data: project
                }));
            }
        })
    }

    handleProjectSearch(event: SelectSearchEvent) {
        this.getProjects(event.query);
    }

    handleProjectChange(event: any) {
        this.selectedProjectId = event.value;
        this.selectedProject = this.projects.find(p => p._id === event.value) || null;
    }

    linkToProject() {
        this.contractService.linkToProject(this.selectedContract?._id ?? "", this.selectedProjectId).subscribe((res) => {
            if (res) {
                this.toastService.success(`Document linked to project - ${this.selectedProject?.name}`)
                this.fetchContracts();
                this.selectedProject = null
                this.selectedProjectId = ''
                this.selectedContract = null
                this.openProjectLinkDialog = false
            }
        })
    }

    // Helper methods for table view
    getDocumentTypeName(docType: IDocumentType | string): string {
        return (docType as IDocumentType)?.name || '';
    }

    getCreatedByName(user: IUser | string): string {
        const userObj = user as IUser;
        return userObj?.fullName || userObj?.email || '';
    }

    getDocumentStatusName(contract: IContract): string {
        return (contract.documentStatus as IStatus)?.name || '';
    }

    onOptionSelect(option: CustomMenuItem) {
        this.selectedTabIndex.set(option.value);
        this.newDocument();
    }

    openOptionsPanel(event?: Event) {
        if (this.optionsMenu && event) {
            this.optionsMenu.toggle(event as Event);
            this.isOptionsMenuOpen = !this.isOptionsMenuOpen;
        }
    }

    handleTabChange(event: string | number | undefined): void {
        let index: number;

        if (event === undefined || event === null) {
            index = 0;
        } else if (typeof event === 'string') {
            index = parseInt(event, 10);
        } else if (typeof event === 'number') {
            index = event;
        } else {
            // Fallback
            index = 0;
        }

        // // Only update if the index actually changed
        if (this.selectedTabIndex() !== index) {
            this.selectedTabIndex.set(index);
        }
    }

    uploadFiles() {
        if (!this.selectedDocTypeId) {
            this.toastService.error("Please select a document type");
            return
        }

        if (this.uploadedFiles.length === 0) {
            this.toastService.error("Please upload at least one file");
            return
        }

        if (this.uploadFiles.length > 50) {
            this.toastService.error(`Maximum 50 files allowed`,
                `You can only attach up to 50 files. via local upload. Please remove some files before adding more.`);
            return;
        }

        this.loadingState.set(true);
        const formData = new FormData();
        formData.append('linkedDocType', this.selectedDocTypeId);
        if (this.uploadedFiles.length > 1) {
            this.uploadedFiles.forEach(file => {
                formData.append(`files`, file);
            });
        } else {
            formData.append('file', this.uploadedFiles[0]);
        }
        this.contractService.uploadMulitpleContract(formData).subscribe((res) => {
            if (res.success) {
                this.toastService.success("Files uploaded successfully");
                this.uploadedFiles = [];
                this.createVisible = false;
                this.fetchContracts(this.first, this.rows);
            } else {
                this.toastService.error("Error uploading files");
            }
            this.loadingState.set(false);
        })
    }

    closeCreateDocumentDialog() {
        this.createVisible = false;
        this.uploadedFiles = [];
        this.selectedDocTypeId = null;
    }

    // Add to Knowledge Base properties
    addToWorkspaceKnowledgeDialog: {
        visible: boolean;
        uploading: boolean;
    } = {
            visible: false,
            uploading: false
        };

    addToProjectKnowledgeDialog: {
        visible: boolean;
        uploading: boolean;
    } = {
            visible: false,
            uploading: false
        };

    KNOWLEDGE_BASE_SVG = KNOWLEDGE_BASE_SVG;

    getKnowledgeBaseIcon(): string {
        return KNOWLEDGE_BASE_SVG.KNOWLEDGE_BASE_ICON;
    }

    getOpenBookCheckIcon(): string {
        return KNOWLEDGE_BASE_SVG.OPEN_BOOK_CHECK;
    }

    // Add to Workspace Knowledge methods
    openAddToWorkspaceKnowledgeDialog() {
        // Support both bulk selection and single document selection
        if (this.selectedContracts && this.selectedContracts.length > 0) {
            // Bulk selection from action bar
            this.addToWorkspaceKnowledgeDialog.visible = true;
        } else if (this.currentMenuDocument) {
            // Single document selection from menu
            this.addToWorkspaceKnowledgeDialog.visible = true;
        } else {
            this.toastService.error('Please select at least one document');
            return;
        }
    }

    uploadToWorkspaceKnowledge(event: { knowledgeBaseIds?: string[]; projectIds?: string[] }) {
        // Support both bulk selection and single document selection
        let documentIds: string[] = [];

        if (this.selectedContracts && this.selectedContracts.length > 0) {
            // Bulk selection from action bar
            documentIds = this.selectedContracts.map(contract => contract._id);
        } else if (this.currentMenuDocument) {
            // Single document selection from menu
            documentIds = [this.currentMenuDocument.id];
        } else {
            this.toastService.error('Please select at least one document');
            return;
        }

        this.addToWorkspaceKnowledgeDialog.uploading = true;

        // Handle "Without KB" case - call backend with empty KBs and projectIds
        if (event.knowledgeBaseIds && event.knowledgeBaseIds.length === 0) {
            const projectIds = event.projectIds || [];
            this.repositoryService.linkToKnowledgeBase([], undefined, documentIds, projectIds).subscribe({
                next: (res) => {
                    if (res.success) {
                        this.toastService.success(`Document${documentIds.length > 1 ? 's' : ''} linked to workspace successfully`);
                        this.addToWorkspaceKnowledgeDialog.visible = false;
                        this.selectedContracts = [];
                        this.currentMenuDocument = null;
                        this.fetchContracts(this.first, this.rows);
                    } else {
                        this.toastService.error('Failed to link document to workspace');
                    }
                    this.addToWorkspaceKnowledgeDialog.uploading = false;
                },
                error: () => {
                    this.toastService.error('Failed to link document to workspace');
                    this.addToWorkspaceKnowledgeDialog.uploading = false;
                }
            });
            return;
        }

        // Handle "With KB" case
        const knowledgeBaseIds = event.knowledgeBaseIds || [];
        this.repositoryService.linkToKnowledgeBase(knowledgeBaseIds, undefined, documentIds).subscribe({
            next: (res) => {
                if (res.success) {
                    const allAlreadyLinked = res.data?.results?.every((result: any) =>
                        !result.success && result.message?.includes('were already linked')
                    );

                    const count = documentIds.length;
                    if (allAlreadyLinked) {
                        this.toastService.info(`Selected document${count > 1 ? 's were' : ' was'} already linked to the knowledge bases`);
                    } else {
                        this.toastService.success(`Document${count > 1 ? 's' : ''} linked to workspace knowledge successfully`);
                    }
                    this.addToWorkspaceKnowledgeDialog.visible = false;
                    this.selectedContracts = [];
                    this.currentMenuDocument = null;
                } else {
                    this.toastService.error('Failed to link document to workspace knowledge');
                }
                this.addToWorkspaceKnowledgeDialog.uploading = false;
            },
            error: () => {
                this.toastService.error('Failed to link document to workspace knowledge');
                this.addToWorkspaceKnowledgeDialog.uploading = false;
            }
        });
    }

    // Add to Project Knowledge methods
    openAddToProjectKnowledgeDialog() {
        // Support both bulk selection and single document selection
        if (this.selectedContracts && this.selectedContracts.length > 0) {
            // Bulk selection from action bar
            this.addToProjectKnowledgeDialog.visible = true;
        } else if (this.currentMenuDocument) {
            // Single document selection from menu
            this.addToProjectKnowledgeDialog.visible = true;
        } else {
            this.toastService.error('Please select at least one document');
            return;
        }
    }

    getCurrentDocumentProjectId(): string | null {
        // For bulk selection, get project from first selected contract
        if (this.selectedContracts && this.selectedContracts.length > 0) {
            const contract = this.selectedContracts[0];
            const project = contract?.project;
            return typeof project === 'string' ? project : project?._id || null;
        }

        // For single document selection
        if (this.currentMenuDocument) {
            const contract = this.contracts.find(c => c._id === this.currentMenuDocument?.id);
            const project = contract?.project;
            return typeof project === 'string' ? project : project?._id || null;
        }

        return null;
    }

    uploadToProjectKnowledge(event: { knowledgeBaseIds?: string[]; projectIds?: string[] }) {
        // Support both bulk selection and single document selection
        let documentIds: string[] = [];

        if (this.selectedContracts && this.selectedContracts.length > 0) {
            // Bulk selection from action bar
            documentIds = this.selectedContracts.map(contract => contract._id);
        } else if (this.currentMenuDocument) {
            // Single document selection from menu
            documentIds = [this.currentMenuDocument.id];
        } else {
            this.toastService.error('Please select at least one document');
            return;
        }

        this.addToProjectKnowledgeDialog.uploading = true;

        // Handle "Without KB" case
        if (event.knowledgeBaseIds && event.knowledgeBaseIds.length === 0) {
            // Call linkToKnowledgeBase with empty KBs and projectIds
            const projectIds = event.projectIds || [];
            this.repositoryService.linkToKnowledgeBase([], undefined, documentIds, projectIds).subscribe({
                next: (res) => {
                    if (res.success) {
                        const projectText = projectIds.length > 1 ? 'projects' : 'project';
                        this.toastService.success(`Document${documentIds.length > 1 ? 's' : ''} linked to ${projectText} successfully`);
                        this.addToProjectKnowledgeDialog.visible = false;
                        this.selectedContracts = [];
                        this.currentMenuDocument = null;
                        this.fetchContracts(this.first, this.rows);
                    } else {
                        this.toastService.error('Failed to link document to project');
                    }
                    this.addToProjectKnowledgeDialog.uploading = false;
                },
                error: () => {
                    this.toastService.error('Failed to link document to project');
                    this.addToProjectKnowledgeDialog.uploading = false;
                }
            });
            return;
        }

        // Handle "With KB" case
        const knowledgeBaseIds = event.knowledgeBaseIds || [];
        this.repositoryService.linkToKnowledgeBase(knowledgeBaseIds, undefined, documentIds).subscribe({
            next: (res) => {
                if (res.success) {
                    const allAlreadyLinked = res.data?.results?.every((result: any) =>
                        !result.success && result.message?.includes('were already linked')
                    );

                    const count = documentIds.length;
                    if (allAlreadyLinked) {
                        this.toastService.info(`Selected document${count > 1 ? 's were' : ' was'} already linked to the knowledge bases`);
                    } else {
                        this.toastService.success(`Document${count > 1 ? 's' : ''} linked to project knowledge successfully`);
                    }
                    this.addToProjectKnowledgeDialog.visible = false;
                    this.selectedContracts = [];
                    this.currentMenuDocument = null;
                } else {
                    this.toastService.error('Failed to link document to project knowledge');
                }
                this.addToProjectKnowledgeDialog.uploading = false;
            },
            error: () => {
                this.toastService.error('Failed to link document to project knowledge');
                this.addToProjectKnowledgeDialog.uploading = false;
            }
        });
    }

}
